import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Colors } from "../theme";
import {
  getAutoOpenAppUrl,
  getDownloadInfo,
  getOpenAppUrl,
} from "../utils/storeLinks";

function RecipeShare() {
  const { recipeId = "" } = useParams();
  const [showImage, setShowImage] = useState(Boolean(recipeId));
  const appUrl = getOpenAppUrl(recipeId);
  const autoOpenAppUrl = getAutoOpenAppUrl(recipeId);
  const download = getDownloadInfo();
  const imageUrl = useMemo(() => {
    if (!recipeId || typeof window === "undefined") {
      return "";
    }

    return `${window.location.origin}/r/${encodeURIComponent(recipeId)}/image.jpg?v=5`;
  }, [recipeId]);

  useEffect(() => {
    setShowImage(Boolean(recipeId));
  }, [recipeId]);

  useEffect(() => {
    const userAgent =
      typeof navigator === "undefined" ? "" : navigator.userAgent || "";
    const isCrawler =
      /bot|crawler|spider|facebookexternalhit|facebot|twitterbot|slackbot|discordbot|linkedinbot|pinterest|telegrambot|whatsapp|applebot|googlebot|bingbot/i.test(
        userAgent,
      );
    const isIOS =
      /iPhone|iPad|iPod/i.test(userAgent) ||
      (typeof navigator !== "undefined" &&
        navigator.platform === "MacIntel" &&
        navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(userAgent);

    if (
      typeof window === "undefined" ||
      !recipeId ||
      isCrawler ||
      new URLSearchParams(window.location.search).get("autoOpen") === "0" ||
      (!isIOS && !isAndroid)
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      window.location.href = autoOpenAppUrl;
    }, 150);

    return () => window.clearTimeout(timeout);
  }, [autoOpenAppUrl, recipeId]);

  return (
    <Page>
      <ShareLayout>
        {showImage && imageUrl ? (
          <ImageWrap>
            <RecipeImage
              src={imageUrl}
              alt=""
              onError={() => setShowImage(false)}
            />
          </ImageWrap>
        ) : null}
        <Content>
          <Kicker>Shared recipe</Kicker>
          <Title>A recipe is waiting in Bite</Title>
          <Body>Open it in the app to view the full recipe.</Body>
          <Actions>
            <PrimaryButton href={appUrl}>Open in Bite</PrimaryButton>
            <SecondaryButton
              href={download.url}
              target="_blank"
              rel="noreferrer"
            >
              {download.label}
            </SecondaryButton>
          </Actions>
        </Content>
      </ShareLayout>
    </Page>
  );
}

const Page = styled.main`
  min-height: calc(100vh - 170px);
  width: min(100% - 48px, 1060px);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0 80px;

  @media (max-width: 760px) {
    width: min(100% - 30px, 560px);
    align-items: flex-start;
    padding-top: 12px;
  }
`;

const ShareLayout = styled.section`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 390px);
  gap: 62px;
  align-items: center;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

const ImageWrap = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: 8px;
  background: ${Colors.gray};
  border: 1px solid rgba(0, 0, 0, 0.08);
`;

const RecipeImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
`;

const Kicker = styled.p`
  margin: 0;
  color: ${Colors.primaryColors.green};
  font-size: 14px;
  font-weight: 800;
`;

const Title = styled.h1`
  margin: 0;
  color: black;
  font-size: 54px;
  line-height: 0.95;
  letter-spacing: 0;

  @media (max-width: 760px) {
    font-size: 42px;
  }

  @media (max-width: 430px) {
    font-size: 34px;
  }
`;

const Body = styled.p`
  margin: 0;
  color: #626262;
  font-size: 19px;
  line-height: 1.45;

  @media (max-width: 760px) {
    font-size: 17px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;

  @media (max-width: 760px) {
    width: 100%;
  }
`;

const PrimaryButton = styled.a`
  min-width: 150px;
  border-radius: 8px;
  background: black;
  color: ${Colors.white};
  padding: 14px 20px;
  font-size: 16px;
  font-weight: 800;
  text-decoration: none;
  text-align: center;

  @media (max-width: 760px) {
    width: 100%;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: ${Colors.white};
  color: black;
  border: 1px solid rgba(0, 0, 0, 0.2);
`;

export default RecipeShare;
